import { logger } from 'lib/hatchd-utils';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Dialog,
  DialogProps,
  Input,
  Stack,
  Text,
} from 'components/ui';
import { signIn } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { rgba } from 'polished';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PATHS, REDIRECT_URL } from 'settings/config';
import styled from 'styled-components';
import { globalTheme } from 'styles/global-theme';
import * as yup from 'yup';

const LinkGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${globalTheme.space[0]};
  width: max-content;
`;

const SignUpCard = styled(Card)`
  padding: ${globalTheme.space[2]} ${globalTheme.space[3]};
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  max-width: 1290px;
  margin-bottom: ${globalTheme.space[0]};
`;

const SignUpText = styled(Text)`
  text-align: center;
  font-size: ${globalTheme.fontSizes[2]};
`;

const SignUpBtn = styled(Button)`
  width: max-content;
`;

const SignInForm = styled.form`
  margin-top: ${globalTheme.space[1]};
`;

const SignInText = styled(Text)`
  text-align: center;
  font-size: ${globalTheme.fontSizes[2]};
  margin-top: ${globalTheme.space[2]};
`;

interface CreateListingDialogProps
  extends Omit<DialogProps, 'actions' | 'title'> {}

type FormData = {
  email: string;
  password: string;
};
const CreateListingDialog: FC<CreateListingDialogProps> = ({
  isOpen,
  onDismiss,
}) => {
  const router = useRouter();
  const schema = yup.object().shape({
    email: yup
      .string()
      .email('Please enter a valid email address')
      .required('Please enter your email address'),
    password: yup.string().required('Please enter your password'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: yupResolver(schema) });

  const onSubmit = async (data: FormData) => {
    try {
      const response = await signIn<'credentials'>('credentials', {
        username: data.email,
        password: data.password,
        redirect: false,
      });

      // Handle no account / validation error messaging
      if (
        response?.error === 'NO_ACTIVE_ACCOUNT' ||
        response?.error === 'VALIDATION_ERROR'
      ) {
        toast.error('Email or password incorrect');
      }

      // Redirect to verification page, the user is not yet verified
      if (response?.error === 'IS_VERIFIED_ERROR') {
        router.push(`${PATHS.auth.verification}?email=${data.email}`);
      }

      // Something else went wrong, show a generic message
      if (response?.error === 'CredentialsSignin') {
        logger.error(response);
        toast.error('Something has gone wrong, please try again later');
      }
      localStorage.setItem(REDIRECT_URL, PATHS.marketplace.createListing);
      await router.push(PATHS.marketplace.createListing);
      onDismiss?.();
    } catch (error) {
      logger.error(error);
    }
  };

  const handleSignUp = () => {
    localStorage.setItem(REDIRECT_URL, PATHS.marketplace.createListing);
    router.push(PATHS.auth.signUp);
  };

  return (
    <Dialog
      title={'Are you new to Agora Livestock?'}
      isOpen={isOpen}
      onDismiss={onDismiss}
      overlayBackgroundColour={rgba(0, 0, 0, 0.3)}
      actions={
        <Button
          size='small'
          form='createNewListingDialog'
          type='submit'
          isLoading={isSubmitting}
          data-cy='login'
        >
          Login
        </Button>
      }
    >
      <SignUpCard>
        <SignUpText>
          If you are new to Agora Livestock, please create an account.
        </SignUpText>
        <SignUpBtn size='small' onClick={handleSignUp}>
          Create account
        </SignUpBtn>
      </SignUpCard>
      <SignInText>
        If you have already have an account, please login below.
      </SignInText>
      <SignInForm
        id='createNewListingDialog'
        onSubmit={handleSubmit(onSubmit)}
        data-cy='login-form'
      >
        <Stack space={1}>
          <Input
            id='email'
            type='email'
            label='Email'
            placeholder='johndoe@email.com'
            state={errors.email && 'error'}
            message={errors.email?.message}
            {...register('email')}
            data-cy='email'
          />
          <Input
            id='password'
            type='password'
            label='Password'
            placeholder='Enter password'
            state={errors.password && 'error'}
            message={errors.password?.message}
            {...register('password')}
            data-cy='password'
          />
          <LinkGrid>
            <Link
              href={PATHS.auth.forgotPassword}
              data-cy='forgotPasswordButton'
            >
              Forgot password?
            </Link>
          </LinkGrid>
        </Stack>
      </SignInForm>
    </Dialog>
  );
};

export default CreateListingDialog;
